<script setup lang="ts">
import { ref, computed } from 'vue';
import { useQuasar } from 'quasar';
import { isAxiosError, type AxiosError } from 'axios';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';
import { useSchoolStore } from '@/stores/school';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import { downloadFile } from '@/functions/downloadFile';
import type { FaaTestReportResponse } from '@/types/Response/FaaTestReportResponse';
import { useUserStore } from '@/stores/user';
import FsumDialog from '@/components/Utilities/FsumDialog.vue';
import { formatDateTime } from '@/functions/formatDate';

// @todo: retrieve the student from the school store.
const props = defineProps<{
    student: FlightSchoolCourseStudent;
    reports: FaaTestReportResponse[];
    loading: boolean;
}>();

const emit = defineEmits<{(event: 'update:reports'): void}>();

const numPerPage = 2;
const currentPage = ref(1);
const maxPages = computed(() => (props.reports ? Math.ceil(props.reports.length / 2) : 1));

const userStore = useUserStore();
const { isGlobalAdmin } = userStore;

const $q = useQuasar();

const downloadUserFile = async (report: FaaTestReportResponse) => {
    const schoolStore = useSchoolStore();

    try {
        const file = await FlightSchoolRepository.getFaaTestReportFile(
            schoolStore.school as FlightSchool,
            schoolStore.course as FlightSchoolCourse,
            props.student,
            report.fileName,
        );

        downloadFile(file, report.userFileName);
    } catch (error: unknown) {
        let errorMessage = 'An error occurred while downloading the file.';

        if (isAxiosError(error)) {
            const axiosError = error as AxiosError;

            if (axiosError.response?.status === 404) {
                errorMessage = 'File not found.';
            }
        }

        /**
         * @todo this should be replaced when front end error logging is implemented
         * https://gitlab.teamgleim.com/gleim-it/code-repos/ogs3/fsum-vue-app/-/issues/22
         */
        // eslint-disable-next-line no-console
        console.error(error);

        $q.notify({
            color: 'negative',
            message: errorMessage,
        });
    }
};

const deleteReport = async (id: number) => {
    await FlightSchoolRepository.deleteFaaTestReport(
        useSchoolStore().school as FlightSchool,
        useSchoolStore().course as FlightSchoolCourse,
        props.student,
        id,
    );

    emit('update:reports');
};

const promptConfirmDelete = (id: number) => {
    const testId = id;
    $q.dialog({
        component: FsumDialog,
        componentProps: {
            title: 'Delete Test Report?',
            message: 'Are you sure you want to delete this item? This cannot be undone.',
        },
    }).onOk(() => {
        deleteReport(testId);
    });
};
</script>
<template>
    <div class="q-my-auto q-mx-auto q-pa-md bg-white report-list">
        <div class="header q-pl-md q-pb-sm text-weight-bold">
            Previous Reports
        </div>
        <q-list
            v-if="reports.length"
            separator
        >
            <q-item
                v-for="(report, index) in reports"
                v-show="index >= (currentPage - 1) * numPerPage && index < currentPage * numPerPage"
                :key="report.id"
                class="q-pa-md report"
                :class="(index + 1) % numPerPage !== 0 ? 'no-separator' : ''"
                no-wrap
            >
                <q-item-section top>
                    <q-item-label class="text-weight-bold report-header report-score">
                        Score: {{ report.score }}
                    </q-item-label>
                    <q-item-label class="text-weight-bold report-header report-exam-date">
                        Test Date: {{ report.examDate }}
                    </q-item-label>
                    <q-item-label class="text-weight-bold report-header report-recorded-date">
                        Recorded Date: {{ formatDateTime(report.createdAt) }}
                    </q-item-label>
                    <q-item-label class="text-weight-bold report-header q-pb-md report-file">
                        Uploaded File:
                        <q-btn
                            v-if="report.fileName"
                            flat
                            no-caps
                            color="fsum-blue"
                            class="anchor-button q-pl-none"
                            :label="report.userFileName"
                            :ripple="false"
                            @click="downloadUserFile(report)"
                        />
                        <span v-else>None</span>
                    </q-item-label>
                    <q-item-label class="report-body">
                        <q-scroll-area style="height: 150px;">
                            {{ report.comment }}
                        </q-scroll-area>
                    </q-item-label>
                    <q-item-label
                        v-if="isGlobalAdmin"
                        class="report-footer"
                    >
                        <q-btn
                            label="Remove"
                            :ripple="false"
                            class="anchor-button text-body2 q-pl-none q-mt-md delete-btn"
                            color="fsum-blue"
                            no-caps
                            dense
                            flat
                            align="left"
                            @click="promptConfirmDelete(report.id)"
                        />
                    </q-item-label>
                </q-item-section>
            </q-item>
        </q-list>
        <div
            v-else
            class="q-pl-md"
        >
            No reports available
        </div>
        <div class="q-pa-lg flex flex-center">
            <q-pagination
                v-if="reports.length"
                v-model="currentPage"
                :max="maxPages"
                input
                color="fsum-med-blue"
            />
        </div>
        <q-inner-loading :showing="loading" />
    </div>
</template>
<style scoped lang="scss">
.report-list {
    color: var(--fsum-black);
    position: relative;

    .header {
        font-size: 32px;
    }

    .report {
        min-height: 225px;
        max-height: max-content;
        font-size: 14px;

        div.report-header, div.report-body, div.report-footer {
            height: auto;
        }
    }

    :deep(.no-separator) {
        border-top: none;
    }
}
</style>
